"use client";

import React, { useContext } from "react";
import { TabContext } from "../Tab/TabContext";

const pillStyles = {
  active: "bg-lightCyan text-white",
  inactive: "text-lightCyan hover:bg-lightGrey",
};

const tabStyles = {
  active:
    "border-b-2 border-lightCyan text-lightCyan",
  inactive:
    "border-b-2 border-transparent text-darkGrey hover:text-darkBlack hover:border-lightGrey3",
};

const variantMap = {
  pills: pillStyles,
  tabs: tabStyles,
};

/**
 * Nav - Navigation component, works standalone or with Tab.Container.
 *
 * @param {string} variant - 'pills' | 'tabs'
 */
function NavComponent({ children, variant = "pills", className = "", ...props }) {
  return (
    <nav
      className={`flex flex-wrap gap-1 ${className}`.trim()}
      role="tablist"
      {...props}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child, { _variant: variant })
          : child
      )}
    </nav>
  );
}

/**
 * Nav.Item - Wrapper for a single nav link.
 */
function NavItem({ children, className = "", _variant, ...props }) {
  return (
    <div className={className} {...props}>
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child, { _variant })
          : child
      )}
    </div>
  );
}

/**
 * Nav.Link - Clickable navigation link, integrates with Tab.Container via context.
 */
function NavLink({
  children,
  eventKey,
  className = "",
  _variant = "pills",
  disabled = false,
  onClick,
  ...props
}) {
  const tabContext = useContext(TabContext);
  const isActive =
    tabContext && String(tabContext.activeKey) === String(eventKey);
  const styles = variantMap[_variant] || variantMap.pills;

  const handleClick = (e) => {
    e.preventDefault();
    if (disabled) return;
    if (tabContext?.onSelect) tabContext.onSelect(eventKey);
    if (onClick) onClick(e);
  };

  return (
    <a
      href="#"
      role="tab"
      aria-selected={isActive}
      className={`block px-4 py-2 rounded cursor-pointer transition-colors duration-150 no-underline ${
        isActive ? styles.active : styles.inactive
      } ${disabled ? "opacity-50 cursor-not-allowed pointer-events-none" : ""} ${className}`.trim()}
      onClick={handleClick}
      {...props}
    >
      {children}
    </a>
  );
}

export const Nav = Object.assign(NavComponent, {
  Item: NavItem,
  Link: NavLink,
});
